import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "./auth/authUtils";
import { AuthProvider } from "./Contexts/AuthContext";
import { DataProvider } from "./Contexts/DataProvider";
import { GeneralFeedbackProvider } from "./Contexts/GeneralFeedbackProvider";
import { FeedbackProvider } from "./Contexts/FeedbackContext";
import { TelemetryDataProvider } from "./Contexts/TelemetryDataContext";
import { InteractionDataProvider } from "./Contexts/InteractionContext";
import { ChatInputProvider } from "./Contexts/ChatInputContext";
import { BannerDetailsProvider } from "./Contexts/BannerDetailsContext";
import AnimatedLoader from "./components/AnimatedLoader/AnimatedLoader";
import { LiveChatReconnectProvider } from "./Contexts/LiveChatReconnectContext";
import { SessionProvider } from "./Contexts/SessionContext";
import { markLoginPerformance, startLoginPerformance } from "./auth/loginPerformance";
import { AgentListProvider } from "./Contexts/AgentListContext";
//import DebugConsole from "./components/DebugConsole";

// Lazy load the AuthenticateSSO component
const AuthenticateSSO = lazy(() => import("./auth/AuthenticateSSO"));

startLoginPerformance();
markLoginPerformance("pre-auth startup checks started");

// CRITICAL: Preserve tour URL parameters BEFORE authentication redirect
// This ensures tour links work even after MSAL auth flow strips the URL params
(function preserveTourParams() {
  // Skip if helpdesk redirect is active — helpdesk takes priority, avoid dual-flow conflicts
  if (window.location.pathname === '/hc') return;

  const urlParams = new URLSearchParams(window.location.search);
  const tourParam = urlParams.get('tour');
  const stepParam = urlParams.get('step');
  const autoStartParam = urlParams.get('autostart');
  const searchQuery = urlParams.get('search');

  if (tourParam || autoStartParam === 'true') {
    const tourData = {
      tourName: tourParam,
      step: stepParam ? parseInt(stepParam, 10) : 1,
      autoStart: autoStartParam === 'true',
      searchQuery,
      timestamp: Date.now()
    };
    sessionStorage.setItem('pending_tour_params', JSON.stringify(tourData));
  }
})();


// document.referrer and URL params are only available on first page load — MSAL auth redirect destroys them
(function captureLandingSource() {
  // Only capture once per browser session — skip on internal SPA navigations
  if (sessionStorage.getItem('landing_source_captured')) return;

  const urlParams = new URLSearchParams(window.location.search);

  // Signal A: explicit URL param set by SmartAnswer when it opens Eva in a new tab
  const fromSmartAnswer = urlParams.get('fromSmartAnswer') === 'true';

  // Signal B: document.referrer — the URL of the page the user was on before arriving at Eva
  const rawReferrer = document.referrer || '';

  const hostMatchesDomain = (host, domain) =>
    host === domain || host.endsWith(`.${domain}`);

  // Classify into a human-readable bucket
  let referrerType = 'direct'; // default: typed URL, bookmark, or app that strips referrer
  if (fromSmartAnswer) {
    referrerType = 'sparq_smart_answer';
  } else if (rawReferrer) {
    try {
      const refHost = new URL(rawReferrer).hostname.toLowerCase();
      if (hostMatchesDomain(refHost, 'sharepoint.com') || hostMatchesDomain(refHost, 'sparq.uhg.com')) {
        referrerType = 'sharepoint';
      } else if (hostMatchesDomain(refHost, 'teams.microsoft.com') || hostMatchesDomain(refHost, 'teams.office.com')) {
        referrerType = 'teams_web';
      } else if (hostMatchesDomain(refHost, 'uhg.com') || hostMatchesDomain(refHost, 'optum.com')) {
        referrerType = 'internal_intranet';
      } else {
        referrerType = 'external_link';
      }
    } catch {
      referrerType = 'unknown';
    }
  }

  sessionStorage.setItem('landing_source', JSON.stringify({
    referrer: rawReferrer.slice(0, 500),
    referrerType,
    fromSmartAnswer,
    landingPath: window.location.pathname,
    timestamp: Date.now()
  }));
  sessionStorage.setItem('landing_source_captured', 'true');
})();

// Redirect from optum.com to uhg.com
(function redirectToUHG() {
  const currentHost = window.location.hostname;
  const currentUrl = window.location.href;

  if (currentHost === "optum.com" || currentHost.endsWith(".optum.com")) {
    const newUrl = currentUrl.replace("optum.com", "uhg.com");
    // Use 301 redirect by setting location.replace
    window.location.replace(newUrl);
  }
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <DataProvider>
          <GeneralFeedbackProvider>
            <FeedbackProvider>
              <TelemetryDataProvider>
                <InteractionDataProvider>
                  <ChatInputProvider>
                    <BannerDetailsProvider>
                      <SessionProvider>
                        <LiveChatReconnectProvider>
                          <AgentListProvider>
                          <Suspense fallback={
                            <div className="loading-container">
                              <AnimatedLoader ariaLabel="Loading application" />
                            </div>
                          }>
                            <AuthenticateSSO />
                          </Suspense>
                          </AgentListProvider>
                        </LiveChatReconnectProvider>
                      </SessionProvider>
                    </BannerDetailsProvider>
                  </ChatInputProvider>
                </InteractionDataProvider>
              </TelemetryDataProvider>
            </FeedbackProvider>
          </GeneralFeedbackProvider>
        </DataProvider>
      </AuthProvider>
      {/* <DebugConsole /> */}
    </MsalProvider>
);